import { useState, useCallback, useRef, useEffect, type JSX } from 'react';
import { Search, X, Plus } from 'lucide-react';
import clsx from 'clsx';
import { supabase } from '../../lib/supabase';
import { escapeOrIlikeTerm } from '../../lib/postgrestEscape';

function fmt$(v: number | null | undefined): string {
  if (v === null || v === undefined) return '—';
  return `$${Number(v).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

// Search result row read from the products table.
interface SearchProduct {
  id: string;
  isg_sku: string | null;
  title: string | null;
  brand: string | null;
  // U10 A1 — junction-assembled at the fetch (primary product_distributors row →
  // cost_usd); the products.cost_usd read-copy select is out (slice-B drop family).
  cost_usd: number | null;
  upc: string | null;
  asin: string | null;
  product_distributors?: { cost_usd: number | null; is_primary: boolean | null }[] | null;
}

export interface AddProductsPayload {
  productId: string;
  isgSku: string | null;
  costPerUnit: number;
  quantity: number;
}

interface AddProductsModalProps {
  open: boolean;
  onClose: () => void;
  onAdd: (payload: AddProductsPayload) => void;
  existingProductIds?: Set<string>;
}

export default function AddProductsModal({
  open,
  onClose,
  onAdd,
  existingProductIds,
}: AddProductsModalProps): JSX.Element | null {
  const [search, setSearch] = useState<string>('');
  const [results, setResults] = useState<SearchProduct[]>([]);
  const [searching, setSearching] = useState<boolean>(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  const doSearch = useCallback(async (term: string): Promise<void> => {
    if (!term.trim()) {
      setResults([]);
      return;
    }
    setSearching(true);
    // Day 76 P5.1 Phase 9A — escapeOrIlikeTerm adoption per Day 73 P4.1
    // injection-class hardening + BACKLOG follow-up.
    const t = escapeOrIlikeTerm(term.trim());
    const { data, error } = await supabase
      .from('products')
      // U10 cost re-point — the junction embed replaces the products.cost_usd
      // read-copy select (slice-B drop family); primary row assembled below.
      .select('id, isg_sku, title, brand, upc, asin, product_distributors ( cost_usd, is_primary )')
      // K5.3-F9 completion: clearance lots are received inventory (re-acquired via ingestion), never a PO line.
      // U10 A1 note — the predicate rides the RETAINED products.is_clearance_lot
      // column: no queryable per-product acquisition-mode surface exists yet
      // (events are per-variant; the FE clearance model returns at owning
      // units/acquisition events — the register:294 ruling). Its acquisition-mode
      // re-expression pends that model; surfaced at the U10 A1 close.
      .eq('is_clearance_lot', false)
      .eq('product_distributors.is_primary', true)
      .or(`isg_sku.ilike.${t},title.ilike.${t},brand.ilike.${t},upc.ilike.${t},asin.ilike.${t}`)
      .order('isg_sku', { ascending: true })
      .limit(50);
    if (!error) {
      const rows = (data ?? []) as unknown as SearchProduct[];
      // Junction-assemble the effective cost (primary row → cost_usd; no
      // retain-last-known — a product with no primary junction row reads null).
      for (const r of rows) {
        r.cost_usd = r.product_distributors?.[0]?.cost_usd ?? null;
      }
      setResults(rows);
    }
    setSearching(false);
  }, []);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const v = e.target.value;
    setSearch(v);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => doSearch(v), 300);
  };

  const handleAdd = (product: SearchProduct): void => {
    // Day 64.x — AddProductsPayload tightened costPerUnit to non-null
    // (matches AddPOStagingArgs contract); coalesce null DB value to 0.
    // Operator can edit in POBuilder's grid afterward.
    onAdd({
      productId: product.id,
      isgSku: product.isg_sku,
      costPerUnit: product.cost_usd ?? 0,
      quantity: 1,
    });
  };

  // Escape to close + focus input on open — replaces the autoFocus prop
  // (which jsx-a11y/no-autofocus flags; we prefer programmatic focus).
  useEffect(() => {
    if (!open) return undefined;
    inputRef.current?.focus();
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop — a real <button> so it's keyboard-activatable and doesn't
          trip the jsx-a11y rules that a plain onClick-only div does. Escape is
          still wired via the window-level listener above. */}
      <button
        type="button"
        className="absolute inset-0 bg-black/60 cursor-default"
        onClick={onClose}
        aria-label="Close dialog"
        tabIndex={-1}
      />
      {/* Dialog panel — no onClick needed on this element because the
          backdrop is a sibling, not an ancestor. */}
      <div
        className="relative bg-surface-2 border border-border rounded-xl w-[640px] max-h-[70vh] flex flex-col shadow-2xl"
        role="dialog"
        aria-modal="true"
        aria-label="Add Products"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-border-subtle">
          <h3 className="text-sm font-semibold text-ink-bright">Add Products</h3>
          <button
            onClick={onClose}
            className="p-1 text-ink-muted hover:text-ink-primary transition-colors"
            aria-label="Close"
          >
            <X size={16} />
          </button>
        </div>

        {/* Search */}
        <div className="px-5 py-3 border-b border-border-subtle">
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-muted" />
            <input
              ref={inputRef}
              type="text"
              value={search}
              onChange={handleSearchChange}
              placeholder="Search by SKU, title, brand, UPC, ASIN…"
              className="arbi-input w-full pl-9 text-xs"
            />
          </div>
        </div>

        {/* Results */}
        <div className="flex-1 overflow-y-auto px-5 py-2">
          {searching && <div className="text-center py-8 text-ink-muted text-xs">Searching…</div>}
          {!searching && results.length === 0 && search.trim() && (
            <div className="text-center py-8 text-ink-muted text-xs">No products found</div>
          )}
          {!searching && results.length === 0 && !search.trim() && (
            <div className="text-center py-8 text-ink-muted text-xs">Type to search products</div>
          )}
          {results.map((product) => {
            const alreadyAdded = existingProductIds?.has(product.id);
            return (
              <div
                key={product.id}
                className={clsx(
                  'flex items-center gap-3 px-3 py-2.5 rounded-lg mb-1 transition-colors',
                  alreadyAdded ? 'opacity-50' : 'hover:bg-surface-3'
                )}
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-mono text-accent">{product.isg_sku}</span>
                    {product.brand && (
                      <span className="text-2xs text-ink-muted">{product.brand}</span>
                    )}
                  </div>
                  <div className="text-xs text-ink-primary truncate mt-0.5">
                    {product.title || '—'}
                  </div>
                </div>
                <span className="text-xs font-mono text-ink-secondary whitespace-nowrap">
                  {fmt$(product.cost_usd)}
                </span>
                <button
                  onClick={() => handleAdd(product)}
                  disabled={alreadyAdded}
                  className={clsx(
                    'arbi-btn text-2xs px-2 py-1',
                    alreadyAdded
                      ? 'bg-surface-4 text-ink-muted'
                      : 'bg-accent/15 text-accent hover:bg-accent/25'
                  )}
                >
                  {alreadyAdded ? (
                    'Added'
                  ) : (
                    <>
                      <Plus size={12} /> Add
                    </>
                  )}
                </button>
              </div>
            );
          })}
        </div>

        {/* Footer */}
        <div className="px-5 py-3 border-t border-border-subtle flex justify-end">
          <button onClick={onClose} className="arbi-btn-ghost text-xs">
            Done
          </button>
        </div>
      </div>
    </div>
  );
}
